import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Mic,
  Square,
  Loader2,
  Sparkles,
  ClipboardCopy,
  AlertTriangle,
  FileText,
  Trash2,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";
import { useVoiceInput } from "@/hooks/use-voice-input";
import { appendVoiceChunk } from "@/lib/voice";

type Template = "consultation" | "soap" | "handover";

interface DictationWorkspaceProps {
  audience: "doctor" | "nurse";
  accentClass: string;
  primaryBtnClass: string;
  title: string;
  subtitle: string;
}

const AI_FOOTNOTE =
  "This note was drafted with AI assistance from a clinician's voice dictation. The signing clinician has reviewed, edited, and confirmed its accuracy.";

export function DictationWorkspace({ audience, accentClass, primaryBtnClass, title, subtitle }: DictationWorkspaceProps) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [structuring, setStructuring] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [draft, setDraft] = useState("");
  const [confirmed, setConfirmed] = useState("");
  const [confirmedSavedAt, setConfirmedSavedAt] = useState<string | null>(null);
  const [template, setTemplate] = useState<Template>("consultation");
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Length of `transcript` at the moment this recording session started.
   *  Used to replace only the live-recognized text with Whisper's higher-
   *  accuracy version when the user stops recording. */
  const sessionStartLenRef = useRef(0);

  const handleLiveChunk = useCallback((chunk: string) => {
    setTranscript((prev) => appendVoiceChunk(prev, chunk));
  }, []);

  const live = useVoiceInput({
    onTranscript: handleLiveChunk,
    onInterim: setInterim,
    lang: "en-GB",
    continuous: true,
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setTranscript("");
      setDraft("");
    };
  }, []);

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await uploadAudio(blob);
      };
      rec.start();
      setRecording(true);
      setElapsed(0);
      setInterim("");
      sessionStartLenRef.current = transcript.length;
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      // Start browser live recognition alongside the recorder, if supported.
      if (live.supported) {
        try { live.start(); } catch { /* ignore — Whisper fallback still runs */ }
      }
    } catch {
      setError("Microphone access was denied or is unavailable.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (live.listening) {
      try { live.stop(); } catch { /* ignore */ }
    }
    setInterim("");
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const uploadAudio = async (blob: Blob) => {
    setTranscribing(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("audio", blob, "dictation.webm");
      form.append("language", "en");
      const res = await fetch("/api/dictation/transcribe", { method: "POST", body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? "Transcription failed.");
      }
      const data = await res.json();
      const whisperText: string = (data.text ?? "").trim();
      // Replace only the live-recognized portion (everything appended since
      // this session's startRecording) with Whisper's more accurate version.
      // Preserves any transcript the user had typed/dictated before pressing
      // Start, and any manual edits they make outside the live window.
      setTranscript((prev) => {
        const base = prev.slice(0, sessionStartLenRef.current);
        if (!whisperText) return prev;
        return appendVoiceChunk(base, whisperText);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Transcription failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const handleStructure = async () => {
    if (!transcript.trim()) return;
    setStructuring(true);
    setError(null);
    try {
      const res = await fetch("/api/dictation/structure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transcript, template, audience }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? "Could not structure the note.");
      }
      const data = await res.json();
      setDraft(data.draft ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not structure the note.");
    } finally {
      setStructuring(false);
    }
  };

  const acceptDraftIntoConfirmed = () => {
    if (!draft) return;
    const withFootnote = `${draft.trim()}\n\n— ${AI_FOOTNOTE}`;
    setConfirmed(withFootnote);
  };

  const copyConfirmed = async () => {
    if (!confirmed) return;
    try {
      await navigator.clipboard.writeText(confirmed);
    } catch {
      /* noop */
    }
  };

  const saveConfirmed = () => {
    if (!confirmed.trim()) return;
    if (!confirmed.includes("AI assistance")) {
      setError("Confirmed notes that used AI assistance must include the AI footnote. Use ‘Accept draft into confirmed note’ or add it manually before saving.");
      return;
    }
    setError(null);
    setConfirmedSavedAt(new Date().toLocaleString("en-GB"));
  };

  const clearAll = () => {
    setTranscript("");
    setDraft("");
    setError(null);
  };

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const templateLabelId = "dictation-template-label";

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl border border-slate-200 p-6 ${accentClass}`}>
        <div className="flex items-start gap-3">
          <Mic className="h-8 w-8 mt-0.5" aria-hidden="true" />
          <div>
            <h2 className="text-xl font-bold">{title}</h2>
            <p className="text-sm opacity-90 mt-0.5">{subtitle}</p>
            <p className="text-xs opacity-80 mt-2">
              Drafts are for clinician review and sign-off. Avoid dictating identifiable patient information; use initials and NHS number references only where appropriate to local IG policy. <strong>GP letters and patient-facing correspondence are out of scope</strong> — those follow your local letter template and sign-off.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Mic className="h-4 w-4" aria-hidden="true" /> Live dictation
          </h3>
          <div className="flex items-center gap-2">
            {recording ? (
              <Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700 text-white gap-2">
                <Square className="h-4 w-4" aria-hidden="true" /> Stop ({String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")})
              </Button>
            ) : (
              <Button onClick={startRecording} disabled={transcribing} className={`${primaryBtnClass} text-white gap-2`}>
                <Mic className="h-4 w-4" aria-hidden="true" /> Start recording
              </Button>
            )}
            {recording && live.listening && (
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1" aria-live="polite">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                Live
              </span>
            )}
            {transcribing && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1" aria-live="polite">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Finalising with Whisper…
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs">
            <span id={templateLabelId} className="text-slate-600 font-medium">Template</span>
            <select
              aria-labelledby={templateLabelId}
              value={template}
              onChange={(e) => setTemplate(e.target.value as Template)}
              className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
            >
              <option value="consultation">Consultation note</option>
              <option value="soap">SOAP</option>
              <option value="handover">SBAR handover</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={clearAll} disabled={!transcript && !draft} className="gap-2 text-xs">
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Clear transcript & draft
            </Button>
            <Button onClick={handleStructure} disabled={!transcript.trim() || structuring} className={`${primaryBtnClass} text-white gap-2 text-xs`}>
              {structuring ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
              {structuring ? "Structuring…" : "Structure note"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Mic className="h-4 w-4" aria-hidden="true" /> Transcript
            </h3>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
              Ephemeral — not saved
            </span>
          </div>
          <label htmlFor="dictation-transcript" className="sr-only">Dictation transcript</label>
          <textarea
            id="dictation-transcript"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={14}
            placeholder="Your dictated transcript will appear here in real time as you speak. You can edit it before structuring."
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y"
          />
          {recording && interim && (
            <p className="text-xs text-slate-500 italic" aria-live="polite">
              <span className="font-medium not-italic text-slate-600">Hearing:</span> {interim}…
            </p>
          )}
          {recording && !live.supported && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
              Live preview is not available in this browser. Your full transcript will appear when you press Stop.
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-4 w-4" aria-hidden="true" /> AI structured draft
            </h3>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
              <ShieldAlert className="h-3 w-3" aria-hidden="true" /> AI draft — review before use
            </span>
          </div>
          {draft ? (
            <>
              <div className="relative">
                <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed border border-slate-100 rounded-lg p-4 bg-slate-50 max-h-[420px] overflow-auto">
                  {draft}
                </pre>
                <span
                  aria-hidden="true"
                  className="pointer-events-none select-none absolute inset-0 flex items-center justify-center text-amber-200/50 text-4xl font-black tracking-widest rotate-[-18deg]"
                >
                  AI DRAFT
                </span>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button onClick={acceptDraftIntoConfirmed} className={`${primaryBtnClass} text-white gap-2 text-xs`}>
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Accept draft into confirmed note
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 italic">Record or paste a transcript, then click Structure note to draft a clean note.</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border-2 border-emerald-200 p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" /> Confirmed clinical note
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={copyConfirmed} disabled={!confirmed} className="gap-2 text-xs">
              <ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" /> Copy
            </Button>
            <Button onClick={saveConfirmed} disabled={!confirmed.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Save confirmed note
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Edit freely before saving. The confirmed note must retain the AI-assistance footnote whenever AI structuring was used; this records that you, the clinician, reviewed the draft.
        </p>
        <label htmlFor="dictation-confirmed" className="sr-only">Confirmed clinical note</label>
        <textarea
          id="dictation-confirmed"
          value={confirmed}
          onChange={(e) => setConfirmed(e.target.value)}
          rows={10}
          placeholder="Paste or accept the AI draft into this field, edit it, then save when ready."
          className="w-full px-3 py-2 text-sm border border-emerald-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y bg-emerald-50/40"
        />
        {confirmedSavedAt && (
          <p className="text-xs text-emerald-700 inline-flex items-center gap-1" aria-live="polite">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Confirmed note ready (local session) — {confirmedSavedAt}
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" role="alert">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export default DictationWorkspace;
